import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { LscArchiveItem, LscLeague, LscMatch } from '../../types';
import { selectLatestPlayableArchive } from '../../archivePlayback';
import { groupArchivesByMatchId, listLscArchive, listLscMatches } from '../../api';
import { lscPath } from '../../routing';
import LeagueHomeHero from './LeagueHomeHero';
import LeagueHomeReplayPlayer from './LeagueHomeReplayPlayer';
import LiveReviewNeededBanner from './LiveReviewNeededBanner';
import RecentArchivesSection from './RecentArchivesSection';
import UpcomingGamesSection from './UpcomingGamesSection';
import MyClaimOpportunitiesPanel from '../MyClaimOpportunitiesPanel';
import LeagueSetupHomeCard from '../LeagueSetupHomeCard';
import { deriveUpcomingMatches } from './leagueHomeUtils';

export type LeagueHomePageProps = {
  leagueId: string;
  league: LscLeague;
  isCommissioner: boolean;
  isOperator: boolean;
  isOwner: boolean;
};

const ARCHIVE_FETCH_LIMIT = 24;

const LeagueHomePage: React.FC<LeagueHomePageProps> = ({
  leagueId,
  league,
  isCommissioner,
  isOperator,
  isOwner,
}) => {
  const [archives, setArchives] = useState<LscArchiveItem[]>([]);
  const [matches, setMatches] = useState<LscMatch[]>([]);
  const [archivesLoading, setArchivesLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [archivesError, setArchivesError] = useState<string | null>(null);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  const [selectedArchive, setSelectedArchive] = useState<LscArchiveItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setArchivesLoading(true);
      setArchivesError(null);
      try {
        const data = await listLscArchive(leagueId, {
          archive_status: 'published',
          limit: ARCHIVE_FETCH_LIMIT,
          group: 'match',
        });
        if (!cancelled) setArchives(data);
      } catch {
        if (!cancelled) setArchivesError('Could not load recent replays.');
      } finally {
        if (!cancelled) setArchivesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMatchesLoading(true);
      setMatchesError(null);
      try {
        const data = await listLscMatches(leagueId, { scope: 'active' });
        if (!cancelled) setMatches(data);
      } catch {
        if (!cancelled) setMatchesError('Could not load upcoming games.');
      } finally {
        if (!cancelled) setMatchesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const archivesByMatchId = useMemo(
    () =>
      groupArchivesByMatchId(
        archives.flatMap((item) => (item.sources?.length ? item.sources : [item]))
      ),
    [archives]
  );
  const upcoming = useMemo(
    () =>
      deriveUpcomingMatches(
        matches,
        archivesByMatchId,
        {
          active_season_label: league.active_season_label,
          active_stage_label: league.active_stage_label,
          active_edition: league.active_edition,
        }
      ),
    [
      matches,
      archivesByMatchId,
      league.active_season_label,
      league.active_stage_label,
      league.active_edition,
    ]
  );
  const fallbackArchive = useMemo(
    () => selectLatestPlayableArchive(archives),
    [archives]
  );

  return (
    <div className="space-y-10" data-testid="league-home-page">
      <div className="flex flex-wrap gap-3" data-testid="league-home-quick-links">
        <Link to={lscPath(`/leagues/${leagueId}/matches`)} className="btn-secondary">
          See full schedule
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/archive`)} className="btn-secondary">
          View archive
        </Link>
      </div>

      <LiveReviewNeededBanner leagueId={leagueId} isCommissioner={isCommissioner} />

      {isOperator ? (
        <LeagueSetupHomeCard leagueId={leagueId} isOwner={isOwner} />
      ) : null}

      <LeagueHomeHero leagueId={leagueId} fallbackArchive={fallbackArchive} />

      <LeagueHomeReplayPlayer leagueId={leagueId} archive={selectedArchive} />

      <RecentArchivesSection
        leagueId={leagueId}
        archives={archives}
        loading={archivesLoading}
        error={archivesError}
        onSelectArchive={setSelectedArchive}
      />

      <UpcomingGamesSection
        leagueId={leagueId}
        upcoming={upcoming}
        loading={matchesLoading}
        error={matchesError}
        emptyMessage={
          (league.active_season_label ?? '').trim()
            ? undefined
            : "This league doesn't have a current season yet."
        }
      />

      {!isCommissioner ? (
        <div data-testid="member-utility-strip">
          <MyClaimOpportunitiesPanel leagueId={leagueId} compactEmpty />
        </div>
      ) : null}
    </div>
  );
};

export default LeagueHomePage;
